import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { PAYMENT_METHOD_LABELS, can, type PaymentMethod } from "@devengo/shared";

import {
  Badge,
  Button,
  Card,
  ErrorBox,
  Input,
  PageTitle,
  numClass,
  tableClass,
  tdClass,
  thClass,
  NoAccess,
} from "../components/ui";
import { api } from "../lib/api";
import { useCurrentUser } from "../lib/session";
import { formatDate, formatDecimal } from "../lib/format";

interface Dashboard {
  today: string;
  totalOwed: string;
  totalOverdue: string;
  monthCharged: string;
  monthCollected: string;
  debtors: {
    companyId: string;
    legalName: string;
    owed: string;
    overdue: string;
    oldestUnpaidDate: string | null;
  }[];
  inFavour: { companyId: string; legalName: string; favour: string }[];
  lastPayments: {
    id: string;
    companyId: string;
    legalName: string;
    date: string;
    amount: string;
    method: string;
  }[];
  companies: { companyId: string; legalName: string; balance: string }[];
}

interface Concept {
  id: string | null;
  name: string;
  archived: boolean;
  stored: boolean;
}

function AccountLink({ companyId, children }: { companyId: string; children: React.ReactNode }) {
  return (
    <Link
      to="/cobranzas/$companyId"
      params={{ companyId }}
      className="text-brand-primary hover:underline"
    >
      {children}
    </Link>
  );
}

function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "danger" | "warning" | undefined;
}) {
  const color =
    tone === "danger" ? "text-red-700" : tone === "warning" ? "text-amber-800" : "text-brand-text";
  return (
    <div className="rounded-lg border border-brand-line bg-white px-4 py-3 shadow-sm">
      <p className="text-xs uppercase tracking-wide text-brand-muted">{label}</p>
      <p className={`text-2xl font-semibold tabular ${color}`}>$ {formatDecimal(value)}</p>
    </div>
  );
}

/** Cobranzas: how much there is to collect across the studio (spec 015 Story 4). Dueño only. */
export function ReceivablesPage() {
  const user = useCurrentUser();
  if (!can(user.role, "manageReceivables")) return <NoAccess />;
  return <ReceivablesBoard />;
}

function ReceivablesBoard() {
  const board = useQuery({
    queryKey: ["receivables", "dashboard"],
    queryFn: () => api.get<Dashboard>("/api/receivables/dashboard"),
  });
  const data = board.data;
  return (
    <>
      <PageTitle subtitle="Lo que el estudio les cobra a sus clientes: cuánto hay por cobrar, quién debe y qué entró.">
        Cobranzas
      </PageTitle>
      <ErrorBox error={board.error} />
      {data ? (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Tile
              label="Por cobrar"
              value={data.totalOwed}
              tone={Number(data.totalOwed) > 0 ? "warning" : undefined}
            />
            <Tile
              label="Vencido"
              value={data.totalOverdue}
              tone={Number(data.totalOverdue) > 0 ? "danger" : undefined}
            />
            <Tile label="Facturado este mes" value={data.monthCharged} />
            <Tile label="Cobrado este mes" value={data.monthCollected} />
          </div>
          <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card title="Los que más deben">
              {data.debtors.length === 0 ? (
                <p className="text-sm text-brand-muted">Nadie debe nada.</p>
              ) : (
                <table className={tableClass}>
                  <thead>
                    <tr>
                      <th className={thClass}>Cliente</th>
                      <th className={`${thClass} text-right`}>Debe</th>
                      <th className={`${thClass} text-right`}>Vencido</th>
                      <th className={thClass}>Impago desde</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.debtors.slice(0, 10).map((d) => (
                      <tr key={d.companyId}>
                        <td className={tdClass}>
                          <AccountLink companyId={d.companyId}>{d.legalName}</AccountLink>
                        </td>
                        <td className={`${tdClass} ${numClass}`}>{formatDecimal(d.owed)}</td>
                        <td
                          className={`${tdClass} ${numClass} ${Number(d.overdue) > 0 ? "text-red-700" : ""}`}
                        >
                          {formatDecimal(d.overdue)}
                        </td>
                        <td className={`${tdClass} tabular`}>
                          {d.oldestUnpaidDate ? formatDate(d.oldestUnpaidDate) : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
            <Card title="Últimos cobros">
              {data.lastPayments.length === 0 ? (
                <p className="text-sm text-brand-muted">Todavía no hay cobros registrados.</p>
              ) : (
                <table className={tableClass}>
                  <thead>
                    <tr>
                      <th className={thClass}>Fecha</th>
                      <th className={thClass}>Cliente</th>
                      <th className={thClass}>Medio</th>
                      <th className={`${thClass} text-right`}>Importe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.lastPayments.map((p) => (
                      <tr key={p.id}>
                        <td className={`${tdClass} tabular`}>{formatDate(p.date)}</td>
                        <td className={tdClass}>
                          <AccountLink companyId={p.companyId}>{p.legalName}</AccountLink>
                        </td>
                        <td className={tdClass}>
                          {PAYMENT_METHOD_LABELS[p.method as PaymentMethod] ?? p.method}
                        </td>
                        <td className={`${tdClass} ${numClass}`}>{formatDecimal(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>
          {data.inFavour.length ? (
            <Card title="Con saldo a favor" className="mb-4">
              <ul className="flex flex-wrap gap-3 text-sm">
                {data.inFavour.map((f) => (
                  <li key={f.companyId}>
                    <AccountLink companyId={f.companyId}>{f.legalName}</AccountLink>{" "}
                    <Badge tone="success">$ {formatDecimal(f.favour)}</Badge>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <Card title="Todos los clientes">
                <table className={tableClass}>
                  <thead>
                    <tr>
                      <th className={thClass}>Cliente</th>
                      <th className={`${thClass} text-right`}>Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.companies.map((c) => (
                      <tr key={c.companyId}>
                        <td className={tdClass}>
                          <AccountLink companyId={c.companyId}>{c.legalName}</AccountLink>
                        </td>
                        <td className={`${tdClass} ${numClass}`}>
                          {Number(c.balance) < 0 ? (
                            <span className="text-emerald-800">
                              a favor {formatDecimal(c.balance.replace(/^-/, ""))}
                            </span>
                          ) : (
                            formatDecimal(c.balance)
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>
            <ConceptsCard />
          </div>
        </>
      ) : null}
    </>
  );
}

function ConceptsCard() {
  const client = useQueryClient();
  const [name, setName] = useState("");
  const concepts = useQuery({
    queryKey: ["receivables", "concepts"],
    queryFn: () => api.get<Concept[]>("/api/receivables/concepts"),
  });
  const refresh = () => client.invalidateQueries({ queryKey: ["receivables", "concepts"] });
  const create = useMutation({
    mutationFn: (value: string) => api.post("/api/receivables/concepts", { name: value }),
    onSuccess: async () => {
      setName("");
      await refresh();
    },
  });
  const archive = useMutation({
    mutationFn: (id: string) => api.post(`/api/receivables/concepts/${id}/archive`),
    onSuccess: refresh,
  });
  return (
    <Card title="Conceptos que cobra el estudio">
      <ul className="mb-3 space-y-1 text-sm">
        {concepts.data
          ?.filter((c) => !c.archived)
          .map((c) => (
            <li key={c.name} className="flex items-center justify-between gap-2">
              <span>
                {c.name}{" "}
                {c.stored ? null : <span className="text-xs text-brand-muted">(sugerido)</span>}
              </span>
              {c.stored && c.id ? (
                <Button variant="ghost" onClick={() => archive.mutate(c.id as string)}>
                  Archivar
                </Button>
              ) : (
                <Button variant="ghost" onClick={() => create.mutate(c.name)}>
                  Guardar
                </Button>
              )}
            </li>
          ))}
      </ul>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) create.mutate(name.trim());
        }}
      >
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nuevo concepto"
        />
        <Button type="submit" disabled={!name.trim() || create.isPending}>
          Agregar
        </Button>
      </form>
      <ErrorBox error={create.error ?? archive.error} />
    </Card>
  );
}
